import React, { useEffect } from 'react';
import { Navigate, Outlet, useLocation, useNavigate } from 'react-router';
import { Trans } from '@lingui/react/macro';

// Material UI
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';

// Components
import { useUserContext } from '../../../providers/user/user.provider';
import { useAuthContext } from '../../../providers/auth.provider';
import { ROUTE_PATHS } from '../_utils/router.utils';
import { useRecommendationContext } from '../../../providers/recommendations/recommendation.provider';
import { LenderLoader, PreQualLoader } from '../../Layout/LenderLoader';
import { LoanLoader } from '../../Layout/LoanLoader';
import { useLocationContext } from '../../../providers/location.provider';
import { DashboardContextProvider } from '../../../components/Optimizer/Dashboard/dashboard-context';
import { SnackBarProvider } from '../../Snackbar/useSnackBar';
import { Column } from '../../page-components-copy/components';
import { hasValidSubscription } from '../../../providers/onboarding/onboarding.utils';
import styled from 'styled-components';
import { SOLVE_LOAN_TYPE } from '@/utils';
import { captureRouteGuardRedirect } from '../../../analytics/sentry';
import Header from '../../Layout/Navigation/Header';
import DashboardPageNav from '../../Optimizer/Dashboard/DashboardPageNav';
import { FranklinLoanModal } from '../../Loans/Franklin/FranklinLoanModal';

const RETURN_TO_KEY = 'solve_returnTo';

const IGNORE_LOADER_ROUTES = [
  ROUTE_PATHS.ACCOUNT,
  ROUTE_PATHS.UPGRADE,
  ROUTE_PATHS.MY_DEBTS,
  ROUTE_PATHS.CREDIT_SCORE,
  ROUTE_PATHS.RENT_REPORTING,
  ROUTE_PATHS.REFERRAL_DASHBOARD,
];

export const DashboardLayout = () => {
  const location = useLocation();
  const navigate = useNavigate();

  window.onbeforeunload = null;

  const { firebaseUser, initialized: authInitialized } = useAuthContext();
  const { user, initialized: userInitialized } = useUserContext();
  const { loading, initialized: planInitialized } = useRecommendationContext();
  const { isPuertoRico } = useLocationContext();

  // Users who expressed loan interest or are in Puerto Rico aren't shopped
  // across a marketplace of lenders, so they see the pre-qualification loader
  // (credit analysis) rather than the "shopping across lenders" animation.
  const isPreQualLoader = isPuertoRico || user?.loanInterestStatus === true;

  const isUnblocked =
    IGNORE_LOADER_ROUTES.includes(location.pathname.split('?')[0]) ||
    location.pathname.includes('/dashboard/make-a-payment');
  const isPersonalLoanApplication = location.pathname.includes(ROUTE_PATHS.LOAN_APPLICATION);
  const isCreditBuilderApplication = location.pathname.includes(ROUTE_PATHS.CREDIT_BUILDER);
  const isShortTermLoanApplication = location.pathname.includes(ROUTE_PATHS.SHORT_TERM_LOAN);
  const isLoanApplication =
    isPersonalLoanApplication ||
    isCreditBuilderApplication ||
    isShortTermLoanApplication ||
    location.pathname.includes(ROUTE_PATHS.LOANS_MANAGEMENT);
  const hasOnboarded = user?.hasOnboarded;

  let isReady = planInitialized && !loading;
  if (isLoanApplication) {
    isReady = hasOnboarded;
  } else if (isUnblocked) {
    isReady = true;
  }

  const shouldRedirectToAuth = authInitialized && !firebaseUser;
  const shouldRedirectToOnboarding =
    !shouldRedirectToAuth && userInitialized && (!user?.hasBureauAuth || !hasValidSubscription(user));

  // Store intended destination when auth gating kicks in
  useEffect(() => {
    if (shouldRedirectToAuth || shouldRedirectToOnboarding) {
      localStorage.setItem(RETURN_TO_KEY, location.pathname + location.search);
    }
  }, [shouldRedirectToAuth, shouldRedirectToOnboarding]);

  // Consume stored destination once dashboard is ready
  useEffect(() => {
    if (!isReady || shouldRedirectToAuth || shouldRedirectToOnboarding) return;

    const returnTo = localStorage.getItem(RETURN_TO_KEY);
    if (returnTo) {
      localStorage.removeItem(RETURN_TO_KEY);
      if (returnTo !== location.pathname + location.search) {
        navigate(returnTo, { replace: true });
      }
    }
  }, [isReady, shouldRedirectToAuth, shouldRedirectToOnboarding]);

  if (shouldRedirectToAuth) return <Navigate to={ROUTE_PATHS.AUTHENTICATE} />;
  if (shouldRedirectToOnboarding) return <Navigate to={ROUTE_PATHS.ONBOARDING} />;

  if (
    isPersonalLoanApplication &&
    hasOnboarded &&
    !user?.solveLoans?.some((loan) => loan.loanType === SOLVE_LOAN_TYPE.PERSONAL)
  ) {
    captureRouteGuardRedirect('no_personal_loan_redirect', {
      from: location.pathname,
      solveLoanTypes: user?.solveLoans?.map((loan) => loan.loanType),
    });
    return <Navigate to={ROUTE_PATHS.DASHBOARD} />;
  }

  if (
    isCreditBuilderApplication &&
    hasOnboarded &&
    !user?.solveLoans?.some((loan) => loan.loanType === SOLVE_LOAN_TYPE.CREDIT_BUILDER)
  ) {
    captureRouteGuardRedirect('no_credit_builder_loan_redirect', {
      from: location.pathname,
      solveLoanTypes: user?.solveLoans?.map((loan) => loan.loanType),
    });
    return <Navigate to={ROUTE_PATHS.DASHBOARD} />;
  }

  let content = isReady ? (
    <Outlet />
  ) : isLoanApplication ? (
    <LoanLoader />
  ) : isPreQualLoader ? (
    <PreQualLoader />
  ) : (
    <LenderLoader />
  );

  if (!authInitialized || !userInitialized) {
    content = (
      <Column>
        <Box textAlign="center" my={6}>
          <h2 className="text-lg">
            <Trans>Getting things ready...</Trans>
          </h2>
          <Box my={8} mx="auto" width="32px">
            <CircularProgress size={32} />
          </Box>
        </Box>
      </Column>
    );
  }

  return (
    <Wrapper>
      <Header />
      <SnackBarProvider>
        <DashboardContextProvider>
          <MobileScrollPad>{content}</MobileScrollPad>
        </DashboardContextProvider>
      </SnackBarProvider>
      <DashboardPageNav />
      <FranklinLoanModal />
    </Wrapper>
  );
};

const Wrapper = styled('div')`
  margin-top: 64px;
`;

const MobileScrollPad = styled('div')`
  @media (max-width: 767px) {
    padding-bottom: calc(env(safe-area-inset-bottom, 0px) + 72px);
  }
`;
